/**
 * The account-sync loop for trips, with storage and network injected.
 *
 * The website (localStorage) and the app (AsyncStorage + zustand) each give it
 * their own way to read and write the device's trips, its bookkeeping and the
 * /me/trips client; the steps themselves are the same on both, so they live
 * here once. The merge rules are in ./trip-sync.ts.
 */
import type { Trip } from "../types";
import { migrateTrip } from "../state/trip-storage";
import {
  EMPTY_TRIP_ACCOUNT_STATE,
  pendingTripChanges,
  planTripSignIn,
  reconcileTrips,
  tripFingerprint,
  type TripAccountState,
} from "./trip-sync";

export interface TripAccountApi {
  /** The account's trip documents. */
  list(): Promise<Trip[]>;
  save(trip: Trip): Promise<void>;
  remove(id: string): Promise<void>;
  /** Additive merge of a device's trips onto the account. */
  sync(trips: readonly Trip[]): Promise<void>;
}

export interface TripAccountDevice {
  readTrips(): Trip[] | Promise<Trip[]>;
  /** Replace the device's trips (the planner must show them). */
  writeTrips(trips: readonly Trip[]): void | Promise<void>;
  readState(): TripAccountState | Promise<TripAccountState>;
  writeState(state: TripAccountState): void | Promise<void>;
  /**
   * Trips an earlier version hid with "Not now". Only read now, to bring them
   * back on screen; nothing new is put aside.
   */
  readStash(): Trip[] | Promise<Trip[]>;
  writeStash(trips: readonly Trip[]): void | Promise<void>;
}

export type TripSignInOutcome =
  /** Synced (or nothing to do). */
  | { readonly kind: "synced" }
  /** The guest has trips from before signing in: ask, then call keep() or notNow(). */
  | { readonly kind: "offer"; readonly count: number };

/** The state to write, with device-only ids pruned to trips still on the device. */
function nextState(
  uid: string,
  ledger: Record<string, string>,
  only: ReadonlySet<string>,
  trips: readonly Trip[],
): TripAccountState {
  const deviceOnly = trips.filter((t) => only.has(t.id)).map((t) => t.id);
  return deviceOnly.length > 0 ? { owner: uid, ledger, deviceOnly } : { owner: uid, ledger };
}

export function createTripAccountSync(api: TripAccountApi, device: TripAccountDevice) {
  // One run at a time: a push and a pull racing would each read a ledger the
  // other is about to change.
  let queue: Promise<unknown> = Promise.resolve();
  function serial<T>(job: () => Promise<T>): Promise<T> {
    const next = queue.then(job, job);
    queue = next.catch(() => undefined);
    return next;
  }

  async function pullNow(uid: string): Promise<void> {
    if ((await device.readState()).owner !== uid) return;
    const remote = (await api.list()).map(migrateTrip);
    // Read the device's copy only now, after the request, so an edit made
    // while it was in flight is part of the merge rather than overwritten.
    const state = await device.readState();
    if (state.owner !== uid) return;
    const all = await device.readTrips();
    const only = new Set(state.deviceOnly ?? []);
    // A device-only trip the account already has is the account's now.
    for (const r of remote) only.delete(r.id);
    const result = reconcileTrips(
      all.filter((t) => !only.has(t.id)),
      remote,
      state.ledger,
    );
    // Keep the device's order: device-only trips stay where they were, the
    // account's news goes after.
    const merged = new Map(result.trips.map((t) => [t.id, t]));
    const trips: Trip[] = [];
    for (const t of all) {
      if (only.has(t.id)) {
        trips.push(t);
        continue;
      }
      const theirs = merged.get(t.id);
      if (theirs) {
        trips.push(theirs);
        merged.delete(t.id);
      }
    }
    trips.push(...merged.values());
    await device.writeTrips(trips);
    const ledger: Record<string, string> = { ...result.ledger };
    for (const trip of result.upload) {
      try {
        await api.save(trip);
        ledger[trip.id] = tripFingerprint(trip);
      } catch {
        /* Not in the ledger, so it is sent again next time. */
      }
    }
    for (const id of result.remove) {
      try {
        await api.remove(id);
      } catch {
        // Kept in the ledger, or the next pull would read the trip as new on
        // the account and bring the deleted trip back.
        const agreed = state.ledger[id];
        if (agreed) ledger[id] = agreed;
      }
    }
    await device.writeState(nextState(uid, ledger, only, trips));
  }

  async function pushNow(uid: string): Promise<boolean> {
    const state = await device.readState();
    if (state.owner !== uid) return false;
    const all = await device.readTrips();
    const only = new Set(state.deviceOnly ?? []);
    const { upload, remove } = pendingTripChanges(
      all.filter((t) => !only.has(t.id)),
      state.ledger,
    );
    if (upload.length === 0 && remove.length === 0) {
      // A device-only trip deleted here: forget its id, there is nothing to send.
      const ids = new Set(all.map((t) => t.id));
      if ((state.deviceOnly ?? []).some((id) => !ids.has(id))) {
        await device.writeState(nextState(uid, { ...state.ledger }, only, all));
      }
      return false;
    }
    const ledger: Record<string, string> = { ...state.ledger };
    for (const trip of upload) {
      try {
        await api.save(trip);
        ledger[trip.id] = tripFingerprint(trip);
      } catch {
        /* Sent again on the next change or the next pull. */
      }
    }
    for (const id of remove) {
      try {
        await api.remove(id);
        delete ledger[id];
      } catch {
        /* Kept in the ledger, so the delete is tried again. */
      }
    }
    await device.writeState(nextState(uid, ledger, only, all));
    return true;
  }

  /**
   * Trips an earlier "Not now" hid until sign-out, put back on the device.
   * Returns the ids brought back.
   */
  async function restoreStash(): Promise<string[]> {
    const stash = (await device.readStash()).map(migrateTrip);
    if (stash.length === 0) return [];
    const trips = await device.readTrips();
    const have = new Set(trips.map((t) => t.id));
    const back = stash.filter((t) => !have.has(t.id));
    if (back.length > 0) await device.writeTrips([...trips, ...back]);
    await device.writeStash([]);
    return back.map((t) => t.id);
  }

  return {
    /** Fetch the account's trips and merge them with the device's. */
    pull: (uid: string) => serial(() => pullNow(uid)),
    /** Send the device's edits since the last agreement. True when anything was sent. */
    push: (uid: string) => serial(() => pushNow(uid)),

    /** A guest is signed in (fresh sign-in or a returning session). */
    signedIn: (uid: string) =>
      serial(async (): Promise<TripSignInOutcome> => {
        const state = await device.readState();
        const plan = planTripSignIn(await device.readTrips(), state, uid);
        if (plan.kind === "offer") return { kind: "offer", count: plan.trips.length };
        if (plan.kind === "fresh") {
          // Trips left by another account are theirs: off the screen, not merged.
          if (state.owner !== null) await device.writeTrips([]);
          await device.writeState({ owner: uid, ledger: {} });
        } else {
          // Trips hidden by the earlier "Not now" come back, still kept off
          // the account.
          const restored = await restoreStash();
          if (restored.length > 0) {
            await device.writeState({
              ...state,
              deviceOnly: [...(state.deviceOnly ?? []), ...restored],
            });
          }
        }
        await pullNow(uid);
        return { kind: "synced" };
      }),

    /** "Keep them": the device's trips go onto the account, then sync. */
    keep: (uid: string) =>
      serial(async () => {
        // The trips as they are now: any made while the question was up are
        // the guest's too.
        await api.sync(await device.readTrips());
        await device.writeState({ owner: uid, ledger: {} });
        await pullNow(uid);
      }),

    /**
     * "Not now": the guest's trips stay on this device, visible and editable,
     * but are not saved to the account. The answer holds until sign-out (the
     * question is not asked again meanwhile); the next sign-in asks again.
     * Trips planned while signed in are the account's, as always.
     */
    notNow: (uid: string) =>
      serial(async () => {
        await restoreStash();
        const ids = (await device.readTrips()).map((t) => t.id);
        await device.writeState(
          ids.length > 0 ? { owner: uid, ledger: {}, deviceOnly: ids } : { owner: uid, ledger: {} },
        );
        await pullNow(uid);
      }),

    /**
     * Signed out: the account's trips leave the device (a shared device must
     * not show one person's trips to the next); the guest's own, the
     * device-only ones, stay.
     */
    signedOut: () =>
      serial(async () => {
        const state = await device.readState();
        if (state.owner === null) return;
        const only = new Set(state.deviceOnly ?? []);
        const mine = (await device.readTrips()).filter((t) => only.has(t.id));
        const have = new Set(mine.map((t) => t.id));
        const stash = (await device.readStash()).map(migrateTrip).filter((t) => !have.has(t.id));
        await device.writeTrips([...mine, ...stash]);
        await device.writeStash([]);
        await device.writeState(EMPTY_TRIP_ACCOUNT_STATE);
      }),
  };
}

export type TripAccountSync = ReturnType<typeof createTripAccountSync>;
